import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, User, Building2, Mail, Phone, Globe, MapPin, Briefcase, Users,
  DollarSign, Calendar, Target, TrendingUp, AlertTriangle, FileText,
  MessageSquare, Brain, CheckSquare, Activity, RefreshCw, ChevronDown,
  ChevronUp, Plus, Trash2, Upload, Clock, Star, Flag, Zap, Sparkles, Loader2,
  Package
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { apiRequest, getMediaUrl } from '../../lib/api';
import AIPackageBuilder from '../../components/AIPackageBuilder';
import VoiceBriefingButton from '../../components/admin/VoiceBriefingButton';
import AutoOutreachButton from '../../components/admin/AutoOutreachButton';
import '../../styles/ops.css';
import '../../styles/leadProfileParser.css';

const PIPELINE_STATUSES = [
  { id: 'new', label: 'New', color: '#6366F1' },
  { id: 'contacted', label: 'Contacted', color: '#F59E0B' },
  { id: 'qualified', label: 'Qualified', color: '#10B981' },
  { id: 'proposal', label: 'Proposal', color: '#3B82F6' },
  { id: 'negotiation', label: 'Negotiation', color: '#8B5CF6' },
  { id: 'won', label: 'Won', color: '#22C55E' },
  { id: 'lost', label: 'Lost', color: '#EF4444' },
];

const STAGE_CHECKLISTS = {
  new: [
    "Lead source captured",
    "Contact info verified",
    "Request summarized",
    "Assigned to team member",
    "First outreach planned"
  ],
  contacted: [
    "Initial contact made",
    "Notes from conversation added",
    "Need/problem documented",
    "Service interest confirmed",
    "Follow-up scheduled"
  ],
  qualified: [
    "Need validated",
    "Budget discussed",
    "Decision-maker identified",
    "Timeline confirmed",
    "Qualification score completed"
  ],
  proposal: [
    "Scope defined",
    "Pricing approved",
    "Proposal uploaded",
    "Proposal sent",
    "Follow-up reminder set"
  ],
  negotiation: [
    "Objections documented",
    "Proposal revised if needed",
    "Pricing changes logged",
    "Contract draft prepared",
    "Next negotiation date set"
  ],
  won: [
    "Contract signed and uploaded",
    "Invoice/deposit sent",
    "Payment status updated",
    "Kickoff scheduled",
    "Onboarding started",
    "Client success goals recorded"
  ],
  lost: [
    "Loss reason documented",
    "Notes completed",
    "Nurture option decided",
    "Competitor data captured if known"
  ]
};

const slugify = (value = '') => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number' && Number.isNaN(value)) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.some((item) => hasValue(item));
  if (typeof value === 'object') return Object.values(value).some((item) => hasValue(item));
  return true;
};

const STAGE_REQUIREMENTS = {
  contacted: [
    { label: 'fecha de contacto', path: ['stage_data', 'contacted', 'contact_date'] },
    { label: 'método de contacto', path: ['stage_data', 'contacted', 'contact_method'] },
    { label: 'notas de conversación', path: ['stage_data', 'contacted', 'conversation_notes'] },
  ],
  qualified: [
    { label: 'presupuesto confirmado', path: ['stage_data', 'qualified', 'budget_confirmed'] },
    { label: 'timeline confirmado', path: ['stage_data', 'qualified', 'timeline_confirmed'] },
    { label: 'decision maker confirmado', path: ['stage_data', 'qualified', 'decision_maker_confirmed'] },
  ],
  proposal: [
    { label: 'servicios incluidos', path: ['stage_data', 'proposal', 'services_included'] },
    { label: 'precio cotizado', path: ['stage_data', 'proposal', 'price_quoted'] },
  ],
  negotiation: [
    { label: 'objeciones', path: ['stage_data', 'negotiation', 'objections'] },
  ],
  won: [
    { label: 'fecha de cierre', path: ['stage_data', 'won', 'date_won'] },
    { label: 'valor final', path: ['stage_data', 'won', 'final_value'] },
  ],
  lost: [
    { label: 'razón principal', path: ['stage_data', 'lost', 'loss_reason'] },
  ],
};

const getNestedValue = (payload, path) => path.reduce((current, key) => (current && typeof current === 'object' ? current[key] : undefined), payload);

const getMissingStageFields = (lead, status) => (STAGE_REQUIREMENTS[status] || [])
  .filter((requirement) => !hasValue(getNestedValue(lead, requirement.path)))
  .map((requirement) => requirement.label);

const getLeadPrimaryVisual = (lead) => (lead?.files || []).find((file) => file.name === 'scan-crop')?.url
  || (lead?.files || []).find((file) => file.name === 'scan-corrected')?.url
  || (lead?.files || []).find((file) => file.type === 'image')?.url
  || lead?.profile_image
  || null;

// Safe normalization helpers — API/database data may arrive with the wrong
// type (string instead of array, null instead of object, etc.). These keep
// the render path from white-screening on legacy/malformed optional data.
const asArray = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.trim() ? [value] : [];
  if (typeof value === 'object') return Object.values(value);
  return [value];
};

const asObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  return {};
};

const asText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.map(asText).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// Health Score Badge Component
const HealthScoreBadge = ({ score }) => {
  const getColor = () => {
    if (score >= 75) return '#22C55E';
    if (score >= 50) return '#F59E0B';
    return '#EF4444';
  };
  
  return (
    <div className="health-score-badge" style={{ borderColor: getColor() }} data-testid="lead-profile-health-score">
      <span className="score-value" style={{ color: getColor() }}>{score}</span>
      <span className="score-label">/100</span>
    </div>
  );
};

// Health Indicator Pill
const HealthIndicator = ({ label, value, type = 'text' }) => {
  const getIndicatorClass = () => {
    if (type === 'boolean') return value ? 'indicator-positive' : 'indicator-negative';
    if (value === 'high' || value === 'confirmed') return 'indicator-positive';
    if (value === 'medium' || value === 'unknown') return 'indicator-neutral';
    return 'indicator-negative';
  };
  
  const displayValue = type === 'boolean' ? (value ? 'Yes' : 'No') : value;
  
  return (
    <div className={`health-indicator ${getIndicatorClass()}`} data-testid={`lead-profile-indicator-${slugify(label)}`}>
      <span className="indicator-label">{label}</span>
      <span className="indicator-value">{displayValue}</span>
    </div>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    type="button"
    className={`profile-tab ${active ? 'active' : ''}`}
    onClick={onClick}
    data-testid={`lead-profile-tab-${slugify(label)}`}
  >
    <Icon size={16} />
    <span>{label}</span>
  </button>
);

// Checklist Item Component
const ChecklistItem = ({ item, checked, onChange }) => (
  <label className="checklist-item">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={`lead-profile-checklist-${slugify(item)}`}
    />
    <span className="checkmark">
      <CheckSquare size={16} />
    </span>
    <span className="item-text">{item}</span>
  </label>
);

// AI Log Entry Component
const AILogEntry = ({ entry }) => {
  const getIcon = () => {
    switch (entry.type) {
      case 'status_change': return <Activity size={14} />;
      case 'analysis_complete': return <Brain size={14} />;
      case 'reanalysis': return <RefreshCw size={14} />;
      default: return <MessageSquare size={14} />;
    }
  };
  
  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="ai-log-entry">
      <div className="log-icon">{getIcon()}</div>
      <div className="log-content">
        <p>{entry.content}</p>
        <span className="log-time">{formatDate(entry.timestamp)}</span>
      </div>
    </div>
  );
};

// Main Lead Profile Page
class ProfileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error) {
    console.error('LeadProfilePage render error:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="lead-profile-page error-screen" data-testid="lead-profile-page-error-boundary">
          <AlertTriangle size={48} />
          <h2>Could not display this lead profile</h2>
          <p>An unexpected error occurred while rendering this lead. The data itself is safe — try reloading.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
              Reload
            </button>
            <Link to="/admin/dashboard" className="btn btn-outline">
              <ArrowLeft size={18} /> Back to Leads
            </Link>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LeadProfilePage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [lead, setLead] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editedLead, setEditedLead] = useState({});
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [transitionNotes, setTransitionNotes] = useState('');
  const [logoState, setLogoState] = useState({ extracting: false, generating: false, message: '' });
  const [originalStatus, setOriginalStatus] = useState(null);

  const fetchLead = useCallback(async () => {
    try {
      setLoading(true);
      setProfileError(false);
      const data = await apiRequest(`/api/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLead(data);
      setEditedLead(data);
      setOriginalStatus(data.status);
    } catch (error) {
      console.error('Error fetching lead:', error);
      if (error.status === 404) {
        setSaveError('Lead not found.');
      } else if (error.status === 401 || error.status === 403) {
        setSaveError('Session expired. Please log in again.');
      } else if (error.status >= 500 || error.status === 0) {
        setProfileError(true);
        setSaveError('Could not load lead. Server or connection error — please try again.');
      } else {
        setProfileError(true);
        setSaveError('Could not load lead.');
      }
    } finally {
      setLoading(false);
    }
  }, [leadId, token]);

  useEffect(() => {
    if (leadId && token) {
      fetchLead();
    }
  }, [leadId, token, fetchLead]);

  const currentStatus = PIPELINE_STATUSES.find(s => s.id === editedLead.status) || PIPELINE_STATUSES[0];
  const checklist = STAGE_CHECKLISTS[editedLead.status] || [];
  const completedChecklist = asArray(editedLead.stage_data?.[editedLead.status]?.checklist_completed);
  const hasStageChange = Boolean(originalStatus) && editedLead.status !== originalStatus;
  const missingStageFields = getMissingStageFields(editedLead, editedLead.status);
  const leadPrimaryVisual = getLeadPrimaryVisual(editedLead);
  const leadLanguage = asText(editedLead.intelligence_snapshot?.language || editedLead.language || 'en');
  const snapshot = asObject(editedLead.intelligence_snapshot);
  snapshot.pain_points = asArray(snapshot.pain_points);
  snapshot.business_needs = asArray(snapshot.business_needs);
  snapshot.next_steps = asArray(snapshot.next_steps);
  snapshot.weak_points = asArray(snapshot.weak_points);
  snapshot.service_recommendations = asArray(snapshot.service_recommendations);
  snapshot.objection_playbook = asArray(snapshot.objection_playbook);
  snapshot.research_resources = asArray(snapshot.research_resources);
  snapshot.enrichment_sources = asArray(snapshot.enrichment_sources);
  snapshot.outreach_messages = asArray(snapshot.outreach_messages);
  snapshot.package_recommendations = asArray(snapshot.package_recommendations);
  snapshot.scores = asObject(snapshot.scores);
  snapshot.business_intelligence = asObject(snapshot.business_intelligence);
  snapshot.approach_strategy_v2 = asObject(snapshot.approach_strategy_v2);
  snapshot.audio_summaries = asObject(snapshot.audio_summaries);
  const audioSummaries = snapshot.audio_summaries || {};
  editedLead.tags = asArray(editedLead.tags);
  editedLead.red_flags = asArray(editedLead.red_flags);
  editedLead.service_interest = asArray(editedLead.service_interest);
  editedLead.stakeholders = asArray(editedLead.stakeholders);
  editedLead.files = asArray(editedLead.files);
  editedLead.ai_log = asArray(editedLead.ai_log);
  editedLead.analysis_history = asArray(editedLead.analysis_history);
  editedLead.social_links = asObject(editedLead.social_links);
  editedLead.health_indicators = asObject(editedLead.health_indicators);
  editedLead.stage_data = asObject(editedLead.stage_data);

  const updateField = (field, value) => {
    setSaveError('');
    setSaveSuccess('');
    setEditedLead(prev => ({ ...prev, [field]: value }));
  };

  const updateStageData = (stage, field, value) => {
    setSaveError('');
    setSaveSuccess('');
    setEditedLead(prev => ({
      ...prev,
      stage_data: {
        ...prev.stage_data,
        [stage]: {
          ...(prev.stage_data?.[stage] || {}),
          [field]: value
        }
      }
    }));
  };

  const refreshLead = async () => {
    const refreshedLead = await apiRequest(`/api/crm/leads/${leadId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setLead(refreshedLead);
    setEditedLead(refreshedLead);
    setOriginalStatus(refreshedLead.status);
    setProfileError(false);
    return refreshedLead;
  };

  const handleChecklistToggle = async (item, checked) => {
    try {
      await apiRequest(`/api/crm/leads/${leadId}/checklist?stage=${editedLead.status}&item=${encodeURIComponent(item)}&completed=${checked}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newCompleted = checked 
        ? [...completedChecklist, item]
        : completedChecklist.filter(i => i !== item);
      
      updateStageData(editedLead.status, 'checklist_completed', newCompleted);
    } catch (error) {
      console.error('Error updating checklist:', error);
    }
  };

  const handleReanalyze = async () => {
    setIsReanalyzing(true);
    try {
      await apiRequest(`/api/crm/leads/${leadId}/reanalyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      await refreshLead();
      setSaveSuccess('Lead reanalyzed successfully.');
    } catch (error) {
      console.error('Error reanalyzing:', error);
      setSaveError(error.message || 'Could not reanalyze lead.');
    }
    setIsReanalyzing(false);
  };

  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setIsUploadingFile(true);
      setSaveError('');
      const result = await apiRequest(`/api/crm/leads/${leadId}/files/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      setEditedLead((current) => ({
        ...current,
        files: [...(current.files || []), result.file],
      }));
      setSaveSuccess('File uploaded successfully.');
    } catch (error) {
      setSaveError(error.message || 'Could not upload file.');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      setSaveError('');
      await apiRequest(`/api/crm/leads/${leadId}/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      setEditedLead((current) => ({
        ...current,
        files: (current.files || []).filter((item) => item.id !== fileId),
      }));
      setSaveSuccess('File deleted.');
    } catch (error) {
      setSaveError(error.message || 'Could not delete file.');
    }
  };

  const handleLogoAction = async (mode) => {
    const stateKey = mode === 'extract' ? 'extracting' : 'generating';
    const endpoint = mode === 'extract' ? 'extract' : 'generate';

    try {
      setLogoState({ extracting: false, generating: false, message: '' });
      setLogoState((current) => ({ ...current, [stateKey]: true, message: '' }));
      setSaveError('');
      const result = await apiRequest(`/api/crm/leads/${leadId}/logo/${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      await refreshLead();
      setLogoState({ extracting: false, generating: false, message: result.message || 'Logo updated.' });
    } catch (error) {
      setLogoState({ extracting: false, generating: false, message: '' });
      const rawMessage = (error.message || '').toLowerCase();
      const friendlyMessage = rawMessage.includes('blocked') || rawMessage.includes('disrupted')
        ? 'Logo extraction was interrupted. Try again with a sharper card image, or use Generate AI logo if the logo is very small.'
        : (error.message || 'Could not update lead logo.');
      setSaveError(friendlyMessage);
    }
  };

  const handleSetAsProfileImage = async (fileUrl) => {
    if (!fileUrl) return;
    try {
      const updated = { ...editedLead, profile_image: fileUrl };
      setEditedLead(updated);
      await apiRequest(`/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          profile_image: fileUrl,
          name: updated.name,
          email: updated.email,
          phone: updated.phone,
          status: updated.status,
        }),
      });
      setSaveSuccess('Profile image updated.');
      setTimeout(() => setSaveSuccess(''), 2500);
    } catch (err) {
      setSaveError(err.message || 'Could not set profile image.');
    }
  };

  const handleSave = async () => {
    if (hasStageChange && !transitionNotes.trim()) {
      setActiveTab('stage');
      setSaveError('Add transition notes before saving the stage change.');
      return;
    }

    if (hasStageChange && missingStageFields.length) {
      setActiveTab('stage');
      setSaveError(`Complete first: ${missingStageFields.join(', ')}.`);
      return;
    }

    try {
      setIsSaving(true);
      setSaveError('');
      setSaveSuccess('');

      // Build payload without read-only fields
      const { id, created_at, updated_at, last_activity, ai_log, ...payload } = editedLead;

      // If stage changed, we need to first save without the status change, then call status endpoint
      if (hasStageChange) {
        payload.status = originalStatus; // Keep original status for the main update
      }

      await apiRequest(`/api/crm/leads/${leadId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      // If status changed, call the status transition endpoint
      if (hasStageChange) {
        const params = new URLSearchParams({
          status: editedLead.status,
          transition_notes: transitionNotes,
        });

        await apiRequest(`/api/crm/leads/${leadId}/status?${params.toString()}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      await refreshLead();
      setTransitionNotes('');
      setSaveSuccess('Changes saved successfully.');
    } catch (error) {
      setSaveError(error.message || 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const renderOverviewTab = () => (
    <div className="profile-tab-content">
      {/* Health Dashboard */}
      <div className="health-dashboard">
        <div className="health-header">
          <div className="health-score-section">
            <h4>Lead Health</h4>
            <HealthScoreBadge score={editedLead.health_score || 50} />
          </div>
        </div>
        
        <div className="health-indicators-grid">
          <HealthIndicator label="Fit" value={editedLead.health_indicators?.fit || 'unknown'} />
          <HealthIndicator label="Budget" value={editedLead.health_indicators?.budget || 'unknown'} />
          <HealthIndicator label="Urgency" value={editedLead.health_indicators?.urgency || 'unknown'} />
          <HealthIndicator label="Responsiveness" value={editedLead.health_indicators?.responsiveness || 'unknown'} />
          <HealthIndicator label="Decision Maker" value={editedLead.health_indicators?.decision_maker_access} type="boolean" />
          <HealthIndicator label="Close Prob." value={`${editedLead.health_indicators?.close_probability || 50}%`} />
        </div>
        
        {editedLead.next_best_action && (
          <div className="next-action-banner" data-testid="lead-profile-next-action">
            <Zap size={18} />
            <div className="next-action-body">
              <span className="action-label">Next best action</span>
              <p>{editedLead.next_best_action}</p>
              <AutoOutreachButton
                lead={editedLead}
                message={editedLead.next_best_action}
                subject={`Following up — ${editedLead.company || editedLead.name || 'your project'}`}
              />
            </div>
          </div>
        )}
        
        {editedLead.growth_potential && (
          <div className="growth-potential-banner">
            <TrendingUp size={18} />
            <p>{editedLead.growth_potential}</p>
          </div>
        )}
      </div>

      {/* Opportunity Overview */}
      <div className="profile-section">
        <h4><Target size={18} /> Opportunity Summary</h4>
        <div className="form-grid">
          <div className="form-field">
            <label>Services of interest</label>
            <input
              type="text"
              value={(editedLead.service_interest || []).join(', ')}
              onChange={(e) => updateField('service_interest', e.target.value.split(',').map(s => s.trim()))}
              placeholder="Web, SEO, Marketing..."
            />
          </div>
          <div className="form-field">
            <label>Estimated budget (EUR)</label>
            <input
              type="number"
              value={editedLead.estimated_value || ''}
              onChange={(e) => updateField('estimated_value', parseFloat(e.target.value) || null)}
            />
          </div>
          <div className="form-field">
            <label>Desired timeline</label>
            <input
              type="text"
              value={editedLead.desired_timeline || ''}
              onChange={(e) => updateField('desired_timeline', e.target.value)}
              placeholder="Immediate, 1 month, 3 months..."
            />
          </div>
          <div className="form-field">
            <label>Probability (%)</label>
            <input
              type="range"
              min="0"
              max="100"
              value={editedLead.probability || 50}
              onChange={(e) => updateField('probability', parseInt(e.target.value))}
            />
            <span className="range-value">{editedLead.probability || 50}%</span>
          </div>
          <div className="form-field full-width">
            <label>Need description</label>
            <textarea
              value={editedLead.need_description || ''}
              onChange={(e) => updateField('need_description', e.target.value)}
              rows={3}
              placeholder="What exactly do they need?"
            />
          </div>
        </div>
      </div>

      {editedLead.intelligence_snapshot && (
        <div className="profile-section" data-testid="lead-profile-intelligence-snapshot">
          <h4><Brain size={18} /> Intelligence Snapshot</h4>
          {snapshot.executive_summary && (
            <div className="snapshot-copy-block">
              <span className="snapshot-label">Executive summary</span>
              <p>{snapshot.executive_summary}</p>
              <VoiceBriefingButton
                text={snapshot.executive_summary}
                profileId={editedLead.intelligence_profile_id || null}
                sectionKey="executive_summary"
                language={leadLanguage}
                existingUrl={
                  audioSummaries.executive_summary?.url
                  || snapshot.audio_summary_url
                  || null
                }
              />
            </div>
          )}
          {snapshot.industry_analysis && (
            <div className="snapshot-copy-block">
              <span className="snapshot-label">Industry analysis</span>
              <p>{snapshot.industry_analysis}</p>
              <VoiceBriefingButton
                text={snapshot.industry_analysis}
                profileId={editedLead.intelligence_profile_id || null}
                sectionKey="industry_analysis"
                language={leadLanguage}
                existingUrl={audioSummaries.industry_analysis?.url || null}
              />
            </div>
          )}
          <div className="compact-score-row">
            {Object.entries(snapshot.scores || {}).map(([key, value]) => value ? (
              <span key={key} className="score-pill medium">{key}: {value}</span>
            ) : null)}
          </div>
          {(snapshot.pain_points || []).length > 0 && (
            <div className="snapshot-copy-block">
              <span className="snapshot-label">Pain points</span>
              <ul className="ops-list compact">
                {snapshot.pain_points.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <VoiceBriefingButton
                text={snapshot.pain_points.join('. ')}
                profileId={editedLead.intelligence_profile_id || null}
                sectionKey="pain_points"
                language={leadLanguage}
                existingUrl={audioSummaries.pain_points?.url || null}
              />
            </div>
          )}
          {(snapshot.business_needs || []).length > 0 && (
            <div className="snapshot-copy-block">
              <span className="snapshot-label">Business needs</span>
              <ul className="ops-list compact">
                {snapshot.business_needs.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <VoiceBriefingButton
                text={snapshot.business_needs.join('. ')}
                profileId={editedLead.intelligence_profile_id || null}
                sectionKey="business_needs"
                language={leadLanguage}
                existingUrl={audioSummaries.business_needs?.url || null}
              />
            </div>
          )}
        </div>
      )}

      {/* Weak Points — parser-style */}
      {(snapshot.weak_points || []).length > 0 && (
        <div className="profile-section" data-testid="lead-profile-weak-points">
          <h4><AlertTriangle size={18} /> Weak Points</h4>
          <div className="parser-card-grid">
            {snapshot.weak_points.map((wp, i) => (
              <div key={wp.title || `wp-${i}`} className="parser-card">
                <div className="parser-card-head">
                  <h5 className="parser-card-title">{wp.title}</h5>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {wp.category && <span className="parser-chip">{wp.category}</span>}
                    {wp.confidence && (
                      <span className={`parser-chip ${wp.confidence?.toLowerCase() === 'high' ? 'ok' : wp.confidence?.toLowerCase() === 'medium' ? 'warn' : 'danger'}`}>
                        {wp.confidence}
                      </span>
                    )}
                  </div>
                </div>
                {wp.description && <p className="parser-card-desc">{wp.description}</p>}
                {wp.why_it_matters && (
                  <div className="parser-card-section">
                    <div className="parser-card-section-label">Why it matters</div>
                    <div className="parser-card-section-value">{wp.why_it_matters}</div>
                  </div>
                )}
                {wp.recommended_service && (
                  <div className="parser-card-section">
                    <div className="parser-card-section-label">Recommended service</div>
                    <div className="parser-card-section-value">{wp.recommended_service}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Recommendations — parser-style */}
      {(snapshot.service_recommendations || []).length > 0 && (
        <div className="profile-section" data-testid="lead-profile-service-recs">
          <h4><Sparkles size={18} /> Recommended Services</h4>
          <div className="parser-card-grid">
            {snapshot.service_recommendations.map((s, i) => (
              <div key={s.service_name || `svc-${i}`} className="parser-card">
                <div className="parser-card-head">
                  <h5 className="parser-card-title">{s.service_name}</h5>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {s.fit_score !== undefined && <span className="parser-chip ok">Fit {s.fit_score}</span>}
                    {s.objection_risk && (
                      <span className={`parser-chip ${s.objection_risk?.toLowerCase() === 'low' ? 'ok' : s.objection_risk?.toLowerCase() === 'high' ? 'danger' : 'warn'}`}>
                        Risk: {s.objection_risk}
                      </span>
                    )}
                  </div>
                </div>
                {s.reason && <p className="parser-card-desc">{s.reason}</p>}
                {s.positioning && (
                  <div className="parser-card-section">
                    <div className="parser-card-section-label">Positioning</div>
                    <div className="parser-card-section-value">{s.positioning}</div>
                  </div>
                )}
                {s.suggested_offer && (
                  <div className="parser-card-section">
                    <div className="parser-card-section-label">Suggested offer</div>
                    <div className="parser-card-section-value">{s.suggested_offer}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approach Strategy — parser-style */}
      {snapshot.approach_strategy_v2 && Object.keys(snapshot.approach_strategy_v2).length > 0 && (
        <div className="profile-section" data-testid="lead-profile-approach">
          <h4><Target size={18} /> Outreach Strategy</h4>
          {snapshot.approach_strategy_v2.best_opening_line && (
            <div className="snapshot-copy-block">
              <span className="snapshot-label">Suggested opening</span>
              <p style={{ fontStyle: 'italic' }}>&ldquo;{snapshot.approach_strategy_v2.best_opening_line}&rdquo;</p>
              <VoiceBriefingButton
                text={snapshot.approach_strategy_v2.best_opening_line}
                profileId={editedLead.intelligence_profile_id || null}
                sectionKey="approach_opening"
                language={leadLanguage}
                existingUrl={audioSummaries.approach_opening?.url || null}
              />
            </div>
          )}
          <div className="parser-bi-grid">
            {snapshot.approach_strategy_v2.best_approach_angle && (
              <div className="parser-bi-card"><div className="parser-bi-card-label">Best angle</div><div className="parser-bi-card-value">{snapshot.approach_strategy_v2.best_approach_angle}</div></div>
            )}
            {snapshot.approach_strategy_v2.best_contact_method && (
              <div className="parser-bi-card"><div className="parser-bi-card-label">Best contact method</div><div className="parser-bi-card-value">{snapshot.approach_strategy_v2.best_contact_method}</div></div>
            )}
            {snapshot.approach_strategy_v2.recommended_tone && (
              <div className="parser-bi-card"><div className="parser-bi-card-label">Tone</div><div className="parser-bi-card-value">{snapshot.approach_strategy_v2.recommended_tone}</div></div>
            )}
            {snapshot.approach_strategy_v2.suggested_timing && (
              <div className="parser-bi-card"><div className="parser-bi-card-label">Timing</div><div className="parser-bi-card-value">{snapshot.approach_strategy_v2.suggested_timing}</div></div>
            )}
          </div>
        </div>
      )}

      {/* Objection Playbook — parser-style */}
      {(snapshot.objection_playbook || []).length > 0 && (
        <div className="profile-section" data-testid="lead-profile-objections">
          <h4><MessageSquare size={18} /> Objection Playbook</h4>
          <div className="parser-card-grid">
            {snapshot.objection_playbook.map((o, i) => (
              <div key={o.objection || `obj-${i}`} className="parser-card">
                <div className="parser-card-head">
                  <h5 className="parser-card-title">&ldquo;{o.objection}&rdquo;</h5>
                  {o.confidence && <span className="parser-chip">{o.confidence}</span>}
                </div>
                <p className="parser-card-desc">{o.response}</p>
                {o.follow_up_question && (
                  <div className="parser-card-section">
                    <div className="parser-card-section-label">Follow-up</div>
                    <div className="parser-card-section-value">&ldquo;{o.follow_up_question}&rdquo;</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(editedLead.analysis_history || []).length > 0 && (
        <div className="profile-section">
          <h4><Brain size={18} /> Analysis History</h4>
          <div>
            {editedLead.analysis_history.map((entry, index) => (
              <div key={`${entry.timestamp}-${index}`} className="ai-log-entry" data-testid={`lead-profile-analysis-history-${index}`}>
                <div className="log-icon"><Brain size={16} /></div>
                <div className="log-content">
                  <strong>{entry.type}</strong>
                  <p>{entry.summary || 'No additional summary'}</p>
                  <span className="log-time">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'No date'} · health {entry.health_score || editedLead.health_score || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderContactTab = () => (
    <div className="profile-tab-content">
      <div className="profile-section">
        <h4><User size={18} /> Contact Information</h4>
        <div className="form-grid">
          <div className="form-field">
            <label>Full name</label>
            <input
              type="text"
              value={editedLead.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Position / Title</label>
            <input
              type="text"
              value={editedLead.position || ''}
              onChange={(e) => updateField('position', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={editedLead.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Phone</label>
            <input
              type="tel"
              value={editedLead.phone || ''}
              onChange={(e) => updateField('phone', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="profile-section">
        <h4><Building2 size={18} /> Company Information</h4>
        <div className="form-grid">
          <div className="form-field">
            <label>Company name</label>
            <input
              type="text"
              value={editedLead.company || ''}
              onChange={(e) => updateField('company', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Industry</label>
            <input
              type="text"
              value={editedLead.industry || ''}
              onChange={(e) => updateField('industry', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Website</label>
            <input
              type="url"
              value={editedLead.website || ''}
              onChange={(e) => updateField('website', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Company size</label>
            <select
              value={editedLead.company_size || ''}
              onChange={(e) => updateField('company_size', e.target.value)}
            >
              <option value="">Select...</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-500">201-500 employees</option>
              <option value="500+">500+ employees</option>
            </select>
          </div>
          <div className="form-field full-width">
            <label>Address</label>
            <input
              type="text"
              value={editedLead.address || ''}
              onChange={(e) => updateField('address', e.target.value)}
            />
          </div>
        </div>
      </div>
      
      <div className="profile-section">
        <h4><Globe size={18} /> Social Media</h4>
        <div className="form-grid">
          <div className="form-field">
            <label>LinkedIn</label>
            <input
              type="url"
              value={editedLead.social_links?.linkedin || ''}
              onChange={(e) => updateField('social_links', { ...editedLead.social_links, linkedin: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label>Instagram</label>
            <input
              type="text"
              value={editedLead.social_links?.instagram || ''}
              onChange={(e) => updateField('social_links', { ...editedLead.social_links, instagram: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStageTab = () => {
    const stageData = editedLead.stage_data?.[editedLead.status] || {};
    
    return (
      <div className="profile-tab-content">
        <div className="stage-header">
          <div className="stage-badge" style={{ backgroundColor: currentStatus.color }}>
            {currentStatus.label}
          </div>
          <select
            value={editedLead.status}
            onChange={(e) => {
              updateField('status', e.target.value);
              setTransitionNotes('');
            }}
            className="status-select"
            data-testid="lead-profile-status-select"
          >
            {PIPELINE_STATUSES.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        {hasStageChange && (
          <div className="automation-banner" data-testid="lead-profile-automation-banner">
            <div>
              <strong>Automation active</strong>
              <p>
                This change requires transition notes and the minimum fields for stage <strong>{currentStatus.label}</strong>.
              </p>
            </div>
            {missingStageFields.length > 0 && (
              <p className="automation-missing" data-testid="lead-profile-missing-fields">
                Missing: {missingStageFields.join(', ')}.
              </p>
            )}
          </div>
        )}

        {/* Stage-specific checklist */}
        <div className="profile-section">
          <h4><CheckSquare size={18} /> Checklist — {currentStatus.label}</h4>
          <div className="checklist-grid">
            {checklist.map(item => (
              <ChecklistItem
                key={item}
                item={item}
                checked={completedChecklist.includes(item)}
                onChange={(checked) => handleChecklistToggle(item, checked)}
              />
            ))}
          </div>
          <div className="checklist-progress">
            <div 
              className="progress-bar"
              style={{ width: `${(completedChecklist.length / checklist.length) * 100}%` }}
            />
            <span data-testid="lead-profile-checklist-progress">{completedChecklist.length}/{checklist.length} completed</span>
          </div>
        </div>

        {hasStageChange && (
          <div className="profile-section">
            <h4><Activity size={18} /> Transition notes</h4>
            <textarea
              value={transitionNotes}
              onChange={(event) => {
                setTransitionNotes(event.target.value);
                setSaveError('');
              }}
              rows={4}
              placeholder="Explain why this lead is moving to the next stage and what changed."
              className="full-width-textarea"
              data-testid="lead-profile-transition-notes"
            />
          </div>
        )}

        {/* Stage-specific fields based on current status */}
        {editedLead.status === 'new' && (
          <div className="profile-section">
            <h4><Clock size={18} /> Intake Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Lead source</label>
                <input
                  type="text"
                  value={stageData.source_details || editedLead.lead_source || ''}
                  onChange={(e) => updateStageData('new', 'source_details', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Urgency level (1-10)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={stageData.urgency_level || ''}
                  onChange={(e) => updateStageData('new', 'urgency_level', parseInt(e.target.value))}
                />
              </div>
              <div className="form-field full-width">
                <label>Intake summary</label>
                <textarea
                  value={stageData.intake_summary || ''}
                  onChange={(e) => updateStageData('new', 'intake_summary', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'contacted' && (
          <div className="profile-section">
            <h4><Phone size={18} /> Contact Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Contact date</label>
                <input
                  type="datetime-local"
                  value={stageData.contact_date || ''}
                  onChange={(e) => updateStageData('contacted', 'contact_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Contact method</label>
                <select
                  value={stageData.contact_method || ''}
                  onChange={(e) => updateStageData('contacted', 'contact_method', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="phone">Phone</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="meeting">Meeting</option>
                  <option value="video">Video call</option>
                </select>
              </div>
              <div className="form-field">
                <label>Interest level (1-10)</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={stageData.interest_level || ''}
                  onChange={(e) => updateStageData('contacted', 'interest_level', parseInt(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Next follow-up</label>
                <input
                  type="datetime-local"
                  value={stageData.next_followup || ''}
                  onChange={(e) => updateStageData('contacted', 'next_followup', e.target.value)}
                />
              </div>
              <div className="form-field full-width">
                <label>Conversation notes</label>
                <textarea
                  value={stageData.conversation_notes || ''}
                  onChange={(e) => updateStageData('contacted', 'conversation_notes', e.target.value)}
                  rows={4}
                  placeholder="Conversation summary, key points, next step..."
                />
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'qualified' && (
          <div className="profile-section">
            <h4><Star size={18} /> Qualification</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Good fit?</label>
                <select
                  value={stageData.is_good_fit || ''}
                  onChange={(e) => updateStageData('qualified', 'is_good_fit', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </div>
              <div className="form-field">
                <label>Budget confirmed</label>
                <select
                  value={stageData.budget_confirmed || ''}
                  onChange={(e) => updateStageData('qualified', 'budget_confirmed', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="form-field">
                <label>Timeline confirmed</label>
                <select
                  value={stageData.timeline_confirmed || ''}
                  onChange={(e) => updateStageData('qualified', 'timeline_confirmed', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="form-field">
                <label>Decision maker confirmed</label>
                <select
                  value={stageData.decision_maker_confirmed || ''}
                  onChange={(e) => updateStageData('qualified', 'decision_maker_confirmed', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="form-field">
                <label>Lead Score (1-100)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={stageData.lead_score || ''}
                  onChange={(e) => updateStageData('qualified', 'lead_score', parseInt(e.target.value))}
                />
              </div>
              <div className="form-field full-width">
                <label>Risks or blockers</label>
                <textarea
                  value={(stageData.risks || []).join('\n')}
                  onChange={(e) => updateStageData('qualified', 'risks', e.target.value.split('\n').filter(r => r.trim()))}
                  rows={2}
                  placeholder="One risk per line..."
                />
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'proposal' && (
          <div className="profile-section">
            <h4><FileText size={18} /> Proposal Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Proposal version</label>
                <input
                  type="number"
                  min="1"
                  value={stageData.proposal_version || 1}
                  onChange={(e) => updateStageData('proposal', 'proposal_version', parseInt(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Quoted price (EUR)</label>
                <input
                  type="number"
                  value={stageData.price_quoted || ''}
                  onChange={(e) => updateStageData('proposal', 'price_quoted', parseFloat(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Sent date</label>
                <input
                  type="date"
                  value={stageData.sent_date || ''}
                  onChange={(e) => updateStageData('proposal', 'sent_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Expiration date</label>
                <input
                  type="date"
                  value={stageData.expiration_date || ''}
                  onChange={(e) => updateStageData('proposal', 'expiration_date', e.target.value)}
                />
              </div>
              <div className="form-field full-width">
                <label>Services included</label>
                <textarea
                  value={(stageData.services_included || []).join('\n')}
                  onChange={(e) => updateStageData('proposal', 'services_included', e.target.value.split('\n').filter(s => s.trim()))}
                  rows={3}
                  placeholder="One service per line..."
                />
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'negotiation' && (
          <div className="profile-section">
            <h4><MessageSquare size={18} /> Negotiation Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Discount requested</label>
                <input
                  type="text"
                  value={stageData.discount_requested || ''}
                  onChange={(e) => updateStageData('negotiation', 'discount_requested', e.target.value)}
                  placeholder="10%, 500 EUR, etc."
                />
              </div>
              <div className="form-field">
                <label>New pricing</label>
                <input
                  type="number"
                  value={stageData.new_pricing || ''}
                  onChange={(e) => updateStageData('negotiation', 'new_pricing', parseFloat(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Contract status</label>
                <select
                  value={stageData.contract_status || ''}
                  onChange={(e) => updateStageData('negotiation', 'contract_status', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="not_started">Not started</option>
                  <option value="drafting">Drafting</option>
                  <option value="review">In review</option>
                  <option value="ready">Ready to sign</option>
                </select>
              </div>
              <div className="form-field full-width">
                <label>Objections raised</label>
                <textarea
                  value={(stageData.objections || []).join('\n')}
                  onChange={(e) => updateStageData('negotiation', 'objections', e.target.value.split('\n').filter(o => o.trim()))}
                  rows={3}
                  placeholder="One objection per line..."
                />
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'won' && (
          <div className="profile-section">
            <h4><Star size={18} /> Closing Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Close date</label>
                <input
                  type="date"
                  value={stageData.date_won || ''}
                  onChange={(e) => updateStageData('won', 'date_won', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Final value (EUR)</label>
                <input
                  type="number"
                  value={stageData.final_value || ''}
                  onChange={(e) => updateStageData('won', 'final_value', parseFloat(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Deposit (EUR)</label>
                <input
                  type="number"
                  value={stageData.deposit_amount || ''}
                  onChange={(e) => updateStageData('won', 'deposit_amount', parseFloat(e.target.value))}
                />
              </div>
              <div className="form-field">
                <label>Invoice status</label>
                <select
                  value={stageData.invoice_status || ''}
                  onChange={(e) => updateStageData('won', 'invoice_status', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partial payment</option>
                </select>
              </div>
              <div className="form-field">
                <label>Kickoff date</label>
                <input
                  type="date"
                  value={stageData.kickoff_date || ''}
                  onChange={(e) => updateStageData('won', 'kickoff_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Onboarding status</label>
                <select
                  value={stageData.onboarding_status || ''}
                  onChange={(e) => updateStageData('won', 'onboarding_status', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="not_started">Not started</option>
                  <option value="in_progress">In progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {editedLead.status === 'lost' && (
          <div className="profile-section">
            <h4><AlertTriangle size={18} /> Loss Data</h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Loss date</label>
                <input
                  type="date"
                  value={stageData.date_lost || ''}
                  onChange={(e) => updateStageData('lost', 'date_lost', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Primary reason</label>
                <select
                  value={stageData.loss_reason || ''}
                  onChange={(e) => updateStageData('lost', 'loss_reason', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="budget">Budget</option>
                  <option value="timing">Timing</option>
                  <option value="competitor">Competitor</option>
                  <option value="no_response">No response</option>
                  <option value="not_fit">Not a fit</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-field">
                <label>Chosen competitor</label>
                <input
                  type="text"
                  value={stageData.competitor_chosen || ''}
                  onChange={(e) => updateStageData('lost', 'competitor_chosen', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Re-engage later?</label>
                <select
                  value={stageData.reengage_later || ''}
                  onChange={(e) => updateStageData('lost', 'reengage_later', e.target.value)}
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="maybe">Maybe</option>
                </select>
              </div>
              <div className="form-field full-width">
                <label>Loss notes</label>
                <textarea
                  value={stageData.failure_notes || ''}
                  onChange={(e) => updateStageData('lost', 'failure_notes', e.target.value)}
                  rows={3}
                  placeholder="What did we learn? What could we have done differently?"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFilesTab = () => (
    <div className="profile-tab-content">
      <div className="profile-section">
        <div className="section-header">
          <h4><FileText size={18} /> Files</h4>
          <div className="file-actions-row">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              onChange={handleFileUpload}
              className="visually-hidden-input"
              data-testid="lead-profile-file-input"
            />
            <button
              className="btn btn-outline btn-sm"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingFile}
              data-testid="lead-profile-file-upload-button"
            >
              <Upload size={14} /> {isUploadingFile ? 'Uploading...' : 'Upload file'}
            </button>
          </div>
        </div>

        <p className="file-upload-hint" data-testid="lead-profile-file-upload-hint">
          Formats enabled in economy mode: PDF, PNG, JPG, JPEG, and WEBP.
        </p>

        {(editedLead.files || []).length === 0 ? (
          <div className="empty-state">
            <FileText size={32} />
            <p>No attached files yet</p>
          </div>
        ) : (
          <div className="files-list files-list--gallery">
            {editedLead.files.map(file => {
              const isImage = file.type === 'image' || (file.media_type || '').startsWith('image/');
              const isCurrentProfile = editedLead.profile_image === file.url;
              return (
                <div key={file.id} className={`file-item ${isImage ? 'file-item--image' : ''}`} data-testid={`lead-profile-file-${file.id}`}>
                  {isImage ? (
                    <a href={getMediaUrl(file.url)} target="_blank" rel="noreferrer" className="file-thumb">
                      <img
                        src={getMediaUrl(file.url)}
                        alt={file.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </a>
                  ) : (
                    <FileText size={18} />
                  )}
                  <div className="file-info">
                    <a className="file-name file-link" href={getMediaUrl(file.url)} target="_blank" rel="noreferrer">
                      {file.name}
                    </a>
                    <span className="file-type">{file.type}</span>
                    {file.uploaded_at && (
                      <span className="file-meta">Extracted {new Date(file.uploaded_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="file-actions">
                    {isImage && (
                      <button
                        className="btn btn-sm btn-outline"
                        type="button"
                        onClick={() => handleSetAsProfileImage(file.url)}
                        disabled={isCurrentProfile}
                        data-testid={`lead-profile-file-set-profile-${file.id}`}
                        title={isCurrentProfile ? 'Currently the profile image' : 'Set as profile image'}
                      >
                        {isCurrentProfile ? 'Profile image' : 'Set as profile'}
                      </button>
                    )}
                    <button
                      className="btn-icon"
                      type="button"
                      onClick={() => handleDeleteFile(file.id)}
                      aria-label={`Delete ${file.name}`}
                      data-testid={`lead-profile-file-delete-${file.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderNotesTab = () => {
    // Assemble cached BI notes from intelligence snapshot — one entry per
    // meaningful sub-section so the user can play/inspect each on its own.
    const bi = snapshot || {};
    const noteEntries = [];
    if (bi.executive_summary) noteEntries.push({
      key: 'executive_summary', label: 'Executive Summary', text: bi.executive_summary,
    });
    if (bi.industry_analysis) noteEntries.push({
      key: 'industry_analysis', label: 'Industry Analysis', text: bi.industry_analysis,
    });
    if ((bi.pain_points || []).length > 0) noteEntries.push({
      key: 'pain_points', label: 'Pain Points', text: bi.pain_points.join('. '),
    });
    if ((bi.business_needs || []).length > 0) noteEntries.push({
      key: 'business_needs', label: 'Business Needs', text: bi.business_needs.join('. '),
    });
    if ((bi.next_steps || []).length > 0) noteEntries.push({
      key: 'next_steps', label: 'Next Steps', text: bi.next_steps.join('. '),
    });
    if (bi.follow_up_request) noteEntries.push({
      key: 'follow_up_request', label: 'Follow-up Request', text: bi.follow_up_request,
    });
    if (bi.approach_strategy_v2?.value_proposition) noteEntries.push({
      key: 'value_proposition', label: 'Value Proposition', text: bi.approach_strategy_v2.value_proposition,
    });

    return (
      <div className="profile-tab-content">
        {noteEntries.length > 0 && (
          <div className="profile-section" data-testid="lead-profile-cached-bi-notes">
            <h4><Brain size={18} /> Cached BI Notes</h4>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 0 12px' }}>
              Every section generated by the parser is cached here as an individual note. Play the audio to hear it in the detected language ({leadLanguage.toUpperCase()}).
            </p>
            <div className="cached-bi-notes">
              {noteEntries.map((entry) => (
                <div key={entry.key} className="cached-bi-note" data-testid={`lead-profile-cached-note-${entry.key}`}>
                  <div className="cached-bi-note-head">
                    <h5 className="cached-bi-note-title">{entry.label}</h5>
                    <VoiceBriefingButton
                      text={entry.text}
                      profileId={editedLead.intelligence_profile_id || null}
                      sectionKey={entry.key}
                      language={leadLanguage}
                      existingUrl={audioSummaries[entry.key]?.url || null}
                    />
                  </div>
                  <p className="cached-bi-note-body">{entry.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="profile-section">
          <h4><MessageSquare size={18} /> Private Notes</h4>
          <textarea
            value={editedLead.private_notes || ''}
            onChange={(e) => updateField('private_notes', e.target.value)}
            rows={6}
            placeholder="Internal notes visible only to your team..."
            className="full-width-textarea"
          />
        </div>

        <div className="profile-section">
          <h4><Flag size={18} /> Red Flags</h4>
          <textarea
            value={(editedLead.red_flags || []).join('\n')}
            onChange={(e) => updateField('red_flags', e.target.value.split('\n').filter(f => f.trim()))}
            rows={3}
            placeholder="One red flag per line..."
          />
        </div>

        <div className="profile-section">
          <h4><TrendingUp size={18} /> Upsell / Cross-sell Potential</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Upsell potential</label>
              <textarea
                value={editedLead.upsell_potential || ''}
                onChange={(e) => updateField('upsell_potential', e.target.value)}
                rows={2}
              />
            </div>
            <div className="form-field">
              <label>Cross-sell potential</label>
              <textarea
                value={editedLead.cross_sell_potential || ''}
                onChange={(e) => updateField('cross_sell_potential', e.target.value)}
                rows={2}
              />
            </div>
          </div>
        </div>

        <div className="profile-section">
          <h4>Tags</h4>
          <input
            type="text"
            value={(editedLead.tags || []).join(', ')}
            onChange={(e) => updateField('tags', e.target.value.split(',').map(t => t.trim()).filter(t => t))}
            placeholder="tag1, tag2, tag3..."
          />
        </div>
      </div>
    );
  };

  const renderAILogTab = () => (
    <div className="profile-tab-content">
      <div className="profile-section">
        <div className="section-header">
          <h4><Brain size={18} /> AI Log / Progress Brain</h4>
          <button 
            className="btn btn-outline btn-sm"
            onClick={handleReanalyze}
            disabled={isReanalyzing}
            data-testid="lead-profile-reanalyze-secondary-button"
          >
            <RefreshCw size={14} className={isReanalyzing ? 'spinning' : ''} />
            Reanalyze
          </button>
        </div>
        
        <div className="ai-log-container">
          {(editedLead.ai_log || []).length === 0 ? (
            <div className="empty-state">
              <Brain size={32} />
              <p>No entries in the AI log yet</p>
            </div>
          ) : (
            <div className="ai-log-list">
              {[...(editedLead.ai_log || [])].reverse().map((entry, idx) => (
                <AILogEntry key={entry.id || entry.created_at || idx} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="lead-profile-page loading-screen" data-testid="lead-profile-page-loading">
        <Loader2 size={32} className="spinning" />
        <p>Loading lead profile...</p>
      </div>
    );
  }

  if (!lead || profileError) {
    return (
      <div className="lead-profile-page error-screen" data-testid="lead-profile-page-error">
        <AlertTriangle size={48} />
        <h2>{profileError ? 'Could not display this lead profile' : 'Lead not found'}</h2>
        <p>{profileError ? 'A server or connection error prevented the profile from loading. Please try again.' : 'Could not load the requested lead.'}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={() => fetchLead()}>
            Retry
          </button>
          <Link to="/admin/dashboard" className="btn btn-outline">
            <ArrowLeft size={18} /> Back to Leads
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ProfileErrorBoundary>
    <div className="lead-profile-page" data-testid="lead-profile-page">
      {/* Page Header */}
      <div className="lead-profile-page-header">
        <div className="container">
          <div className="lead-profile-header-content">
            <Link to="/admin/dashboard" className="back-link" data-testid="lead-profile-back-link">
              <ArrowLeft size={18} />
              <span>Back to Leads</span>
            </Link>
            
            <div className="lead-profile-header-main">
              <div className="profile-avatar-large">
                {leadPrimaryVisual ? (
                  <img src={getMediaUrl(leadPrimaryVisual)} alt={editedLead.name} />
                ) : (
                  <User size={48} />
                )}
              </div>
              <div className="lead-profile-header-info">
                <h1 data-testid="lead-profile-name">{editedLead.name || 'Unnamed lead'}</h1>
                <p data-testid="lead-profile-company">{editedLead.company} {editedLead.position && `- ${editedLead.position}`}</p>
                <div className="profile-quick-stats">
                  <span className="stage-badge" style={{ backgroundColor: currentStatus.color }} data-testid="lead-profile-current-stage">
                    {currentStatus.label}
                  </span>
                  {editedLead.estimated_value && (
                    <span className="value-badge" data-testid="lead-profile-estimated-value">EUR {editedLead.estimated_value.toLocaleString()}</span>
                  )}
                  <span className="probability-badge" data-testid="lead-profile-probability">{editedLead.probability || 50}%</span>
                </div>
              </div>
              
              {/* Logo Actions - Moved to top right */}
              <div className="lead-profile-header-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => handleLogoAction('extract')}
                  disabled={logoState.extracting || logoState.generating}
                  data-testid="lead-profile-extract-logo-button"
                >
                  <Brain size={14} /> {logoState.extracting ? 'Finding...' : 'Extract logo'}
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => handleLogoAction('generate')}
                  disabled={logoState.extracting || logoState.generating}
                  data-testid="lead-profile-generate-logo-button"
                >
                  <Sparkles size={14} /> {logoState.generating ? 'Generating...' : 'Generate AI logo'}
                </button>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={handleReanalyze}
                  disabled={isReanalyzing}
                  data-testid="lead-profile-reanalyze-button"
                >
                  <RefreshCw size={14} className={isReanalyzing ? 'spinning' : ''} />
                  {isReanalyzing ? 'Analyzing...' : 'Reanalyze'}
                </button>
                {logoState.message && (
                  <span className="logo-action-message" data-testid="lead-profile-logo-message">{logoState.message}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="lead-profile-page-body">
        <div className="container">
          {/* Feedback Messages */}
          {saveError && (
            <div className="error-banner" data-testid="lead-profile-save-error">
              <AlertTriangle size={16} />
              <span>{saveError}</span>
            </div>
          )}
          
          {saveSuccess && (
            <div className="success-banner" data-testid="lead-profile-save-success">
              <Sparkles size={16} />
              <span>{saveSuccess}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="profile-tabs">
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={Target} label="Overview" />
            <TabButton active={activeTab === 'contact'} onClick={() => setActiveTab('contact')} icon={User} label="Contact" />
            <TabButton active={activeTab === 'stage'} onClick={() => setActiveTab('stage')} icon={CheckSquare} label="Stage" />
            <TabButton active={activeTab === 'package'} onClick={() => setActiveTab('package')} icon={Package} label="Package" />
            <TabButton active={activeTab === 'files'} onClick={() => setActiveTab('files')} icon={FileText} label="Files" />
            <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} icon={MessageSquare} label="Notes" />
            <TabButton active={activeTab === 'ailog'} onClick={() => setActiveTab('ailog')} icon={Brain} label="AI Log" />
          </div>

          {/* Tab Content */}
          <div className="lead-profile-content">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'contact' && renderContactTab()}
            {activeTab === 'stage' && renderStageTab()}
            {activeTab === 'package' && (
              <div className="profile-tab-content">
                <AIPackageBuilder 
                  leadId={leadId}
                  leadName={editedLead.name || editedLead.company || 'Lead'}
                  currentStage={editedLead.status}
                  onSave={(packageData) => {
                    setSaveSuccess('Paquete guardado correctamente.');
                    // Update local lead state with package info
                    setEditedLead(prev => ({
                      ...prev,
                      package_analysis: packageData
                    }));
                  }}
                />
              </div>
            )}
            {activeTab === 'files' && renderFilesTab()}
            {activeTab === 'notes' && renderNotesTab()}
            {activeTab === 'ailog' && renderAILogTab()}
          </div>

          {/* Footer Actions */}
          <div className="lead-profile-page-footer">
            <Link to="/admin/dashboard" className="btn btn-outline" data-testid="lead-profile-cancel-button">
              Cancel
            </Link>
            <button 
              className="btn btn-primary" 
              onClick={handleSave} 
              disabled={isSaving} 
              data-testid="lead-profile-save-button"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </ProfileErrorBoundary>
  );
};

export default LeadProfilePage;
